const CACHE_PREFIX = 'openflex_search_';
const SEARCH_HISTORY_KEY = 'openflex_search_history';
const CACHE_VERSION = 2;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

class ClientCacheService {
  constructor() {
    this.memoryCache = new Map();
  }

  generateCacheKey(query) {
    return `${CACHE_PREFIX}${query || 'default'}`;
  }

  getCachedSearch(query) {
    const cacheKey = this.generateCacheKey(query);
    
    // Check memory cache first (faster)
    if (this.memoryCache.has(cacheKey)) {
      const cached = this.memoryCache.get(cacheKey);
      if (cached.version === CACHE_VERSION && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }
      this.memoryCache.delete(cacheKey);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const cached = JSON.parse(stored);
        if (cached.version === CACHE_VERSION && Date.now() - cached.timestamp < CACHE_TTL) {
          // Restore to memory cache
          this.memoryCache.set(cacheKey, cached);
          return cached.data;
        }
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      // Silently fail on localStorage access
    }

    return null;
  }

  cacheSearch(query, data) {
    const cacheKey = this.generateCacheKey(query);
    const cacheEntry = {
      timestamp: Date.now(),
      data: data,
      version: CACHE_VERSION
    };

    // Store in memory cache
    this.memoryCache.set(cacheKey, cacheEntry);

    // Store in localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      // Silently fail if localStorage is full or unavailable
    }

    // Record search in history
    this.recordSearchHistory(query);
  }

  recordSearchHistory(query) {
    if (!query || query.trim().length === 0) return;

    try {
      const historyStr = localStorage.getItem(SEARCH_HISTORY_KEY) || '[]';
      const history = JSON.parse(historyStr);
      
      // Remove if already exists and move to top
      const filtered = history.filter(item => item.query !== query);
      
      // Add to top with timestamp
      const newHistory = [
        { query: query.trim(), timestamp: Date.now() },
        ...filtered
      ].slice(0, 50); // Keep only last 50 searches

      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
    } catch (error) {
      // Silently fail if localStorage is full or unavailable
    }
  }

  getSearchHistory() {
    try {
      const historyStr = localStorage.getItem(SEARCH_HISTORY_KEY) || '[]';
      return JSON.parse(historyStr);
    } catch (error) {
      return [];
    }
  }

  clearCache() {
    this.memoryCache.clear();
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keys.push(key);
        }
      }
      keys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      // Silently fail
    }
  }

  clearOldCaches() {
    const now = Date.now();
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keys.push(key);
        }
      }
      keys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const cached = JSON.parse(stored);
            if (cached.version !== CACHE_VERSION || now - cached.timestamp > CACHE_TTL) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // Silently fail
        }
      });
    } catch (error) {
      // Silently fail
    }
  }
}

const clientCacheService = new ClientCacheService();
export default clientCacheService;
